/**
 * Store: Bed Visualizer
 * -----------------------------------------------------------------------------
 * Bed visualizer.
 * - Tied to the bed visualizer and related components.
 * - Only JSDOC custom parameters, ignore module state or object.
 *
 */

export default {
  namespaced: true,

  /**
   * config.activeFlow - Name of the current flow.
   * config.filters - Global filters applied to all products in the flow.
   * config.selectedFilters - User selected filters unique to each step.
   * config.selectedCollection - Selected collection in collection flow.
   * config.selectedOptions - Selected options for each step.
   * @returns {Object}
   */
  state: () => ({
    config: {
      activeFlow: 'custom',
      filters: {},
      selectedFilters: {},
      selectedCollection: {},
      selectedOptions: {},
    },
  }),

  getters: {

    /**
     * Get the current bed visualizer config object.
     * @returns {Object}
     */
    getConfig(state) {
      const { bedVisualizerConfig } = window.localStorage
      return bedVisualizerConfig ? JSON.parse(bedVisualizerConfig) : state.config
    },
  },

  mutations: {

    /**
     * Reset config.
     * - Resets config to initial state.
     */
    RESET_CONFIG(state) {
      state.config = {
        filters: {},
        selectedFilters: {},
        selectedOptions: {},
        selectedCollection: {},
      }
    },

    /**
     * Save config.
     * - Saves config to local storage.
     */
    SAVE_CONFIG(state) {
      window.localStorage.bedVisualizerConfig = JSON.stringify(state.config)
    },


    /**
     * Remove option from selected options based on key.
     */
    REMOVE_OPTION(state, payload) {
      const { key } = payload
      const optionsObject = state.config.selectedOptions || {}

      delete optionsObject[key];

      state.config.selectedOptions = optionsObject
    },

    /**
     * Update type.
     * - Updates active flow type.
     */
    UPDATE_TYPE(state, type) {
      state.config.activeFlow = type || ''
    },

    /**
     * Update global filters.
     * - Overwrites global filters in config with new values.
     */
    UPDATE_GLOBAL_FILTERS(state, filters) {
      Object.keys(filters).forEach(key => {
        state.config.filters[key] = filters[key] || []
      })
    },

    /**
     * Add filters
     */
    ADD_FILTERS(state, payload) {
      const { step, filters } = payload
      const filterObject = state.config.selectedFilters || {}

      if (!step || !filters) {
        return
      }

      if (!filterObject[step]) {
        filterObject[step] = {}
      }

      Object.keys(filters).forEach(key => {
        if (!Array.isArray(filterObject[step][key])) {
          filterObject[step][key] = []
        }

        const filter = filters[key]

        if (!filterObject[step][key].includes(filter)) {
          filterObject[step][key].push(filter)
        }
      })

      state.config.selectedFilters = filterObject
    },

    /**
     * Remove filters.
     */
    REMOVE_FILTERS(state, payload) {
      const { step, filters } = payload
      const filterObject = state.config.selectedFilters || {}

      if (!step || !filters || !filterObject[step]) {
        return
      }

      Object.keys(filters).forEach(key => {
        if (!Array.isArray(filterObject[step][key])) {
          return
        }

        const filter = filters[key]
        const index = filterObject[step][key].indexOf(filter)

        if (index > -1) {
          filterObject[step][key].splice(index, 1)
        }
      })
    },

    /**
     * Remove filters.
     */
    REMOVE_ALL_FILTERS(state, payload) {
      const { step } = payload
      const filterObject = state.config.selectedFilters || {}

      if (!step || !filterObject[step]) {
        return
      }

      filterObject[step] = {}
      state.config.selectedFilters = filterObject
    },

    /**
     * Use saved config.
     * - Sets state to match saved config.
     */
    USE_SAVED_CONFIG(state) {
      const { bedVisualizerConfig } = window.localStorage

      if (bedVisualizerConfig) {
        state.config = JSON.parse(bedVisualizerConfig)
      }
    },

  },

  actions: {

    /**
     * Reset config.
     */
    resetConfig({ commit }) {
      commit('RESET_CONFIG')
    },

    /**
     * Update type.
     * @param {String} type - Active type.
     */
    updateType({ commit }, type) {
      commit('UPDATE_TYPE', type)
    },

    /**
     * Update global filters.
     * @param {Object} filters - Filters object.
     */
    updateGlobalFilters({ commit }, filters) {
      commit('UPDATE_GLOBAL_FILTERS', filters)
    },

    /**
     * Update filters.
     * @param {String} payload.step - Step name.
     * @param {Object} payload.filters - Filters object.
     */
    updateFilters({ commit }, payload) {
      commit('UPDATE_FILTERS', payload)
    },

    /**
     * Add filters.
     * @param {String} payload.step - Step name.
     * @param {Object} payload.filters - Filters object.
     */
    addFilters({ commit }, payload) {
      commit('ADD_FILTERS', payload)
    },

    /**
     * Remove filters.
     * @param {String} payload.step - Step name.
     * @param {Object} payload.filters - Filters object.
     */
    removeFilters({ commit }, payload) {
      commit('REMOVE_FILTERS', payload)
    },

    removeOption({ commit }, payload) {
      commit('REMOVE_OPTION', payload);
    },

    /**
     * Remove all filters.
     * @param {String} payload.step - Step name.
     * @param {Object} payload.filters - Filters object.
     */
     removeAllFilters({ commit }, payload) {
      commit('REMOVE_ALL_FILTERS', payload)
    },

    /**
     * Use saved config.
     */
    useSavedConfig({ commit }) {
      commit('USE_SAVED_CONFIG')
    },

    /**
     * Save config.
     */
    saveConfig({ commit }) {
      commit('SAVE_CONFIG')
    },
  },
}
